/**
 * Level manifests ⇄ world data.
 *
 * `prepareWorld` is the *prepare* half of a level load (review F-4): it builds
 * a detached WorldData without touching the live world, so a bad manifest can
 * never leave the editor half-loaded.
 *
 * Failure policy:
 * - Structural problems fail the load (LevelFormatError): missing or duplicate
 *   ids, duplicate names (names must work in deep links), unknown components.
 * - Value problems degrade with a warning, like Unreal loading stale
 *   properties: invalid props take their defaults; dangling entity references
 *   become null.
 */
import type { LevelManifest, ManifestEntity } from "../assets/content";
import { entityId } from "./entity";
import { findEntity, invalidNameReason } from "./names";
import type { NamedEntity } from "./names";
import type { EntityId, EntityRecord } from "./entity";
import type { ComponentRegistry } from "./schema/ComponentRegistry";
import type { ComponentData, ComponentSchema } from "./schema/defineComponent";
import type { WorldData, WorldSnapshot } from "./World";

export class LevelFormatError extends Error {
  override name = "LevelFormatError";
}

export interface PreparedWorld {
  readonly data: WorldData;
  readonly warnings: readonly string[];
}

interface FlatEntity {
  readonly source: ManifestEntity;
  readonly parent: EntityId | null;
}

export function prepareWorld(manifest: LevelManifest, registry: ComponentRegistry): PreparedWorld {
  const { records, warnings } = prepareEntities(manifest.entities, null, registry);
  return {
    data: {
      levelId: manifest.id,
      entities: new Map(records.map((record) => [record.id, record])),
      roots: manifest.entities.map((e) => entityId(e.id)),
    },
    warnings,
  };
}

export interface PreparedEntities {
  /** Depth-first, each parent before its children. */
  readonly records: readonly EntityRecord[];
  readonly warnings: readonly string[];
}

/**
 * Records for manifest entities placed under `parent` (null: at the top), with
 * a level load's checks and failure policy. `context` names the entities
 * already in the world, so references out of the new ones resolve to them
 * (creating or pasting a subtree into a level).
 */
export function prepareEntities(
  entities: readonly ManifestEntity[],
  parent: EntityId | null,
  registry: ComponentRegistry,
  context: ReadonlyMap<EntityId, NamedEntity> = new Map(),
): PreparedEntities {
  const flat = flatten(entities, parent);
  const names = new Map([...context, ...indexNames(flat)]);
  const warnings: string[] = [];

  const records = flat.map(({ source, parent }): EntityRecord => {
    const components = parseComponents(source, registry, names, (message) =>
      warnings.push(`${source.name}: ${message}`),
    );
    return {
      id: entityId(source.id),
      name: source.name,
      className: source.class ?? null,
      parent,
      children: (source.children ?? []).map((child) => entityId(child.id)),
      visible: source.visible ?? true,
      components,
      ...(source.disabled && source.disabled.length > 0 && { disabled: new Set(source.disabled) }),
    };
  });

  return { records, warnings };
}

/** Writes the world back out as manifest entities (ids for references, defaults included). */
export function serializeWorld(
  snapshot: WorldSnapshot,
  registry: ComponentRegistry,
): ManifestEntity[] {
  const write = (id: EntityId): ManifestEntity => {
    const record = snapshot.entities.get(id);
    if (!record) throw new Error(`Snapshot is missing entity '${id}'`);

    const components = Object.fromEntries(
      [...record.components]
        .filter(([type]) => registry.has(type))
        .map(([type, data]) => [type, { ...data }]),
    );

    return {
      id: record.id,
      name: record.name,
      ...(record.className !== null && { class: record.className }),
      ...(!record.visible && { visible: false }),
      components,
      ...(record.disabled && record.disabled.size > 0 && { disabled: [...record.disabled] }),
      ...(record.children.length > 0 && { children: record.children.map(write) }),
    };
  };

  return snapshot.roots.map(write);
}

// ─── Prepare helpers ─────────────────────────────────────────────────────

/** Depth-first flattening with parent links, validating ids along the way. */
function flatten(entities: readonly ManifestEntity[], parent: EntityId | null): FlatEntity[] {
  const out: FlatEntity[] = [];
  const seen = new Set<string>();

  const walk = (list: readonly ManifestEntity[], parentId: EntityId | null) => {
    for (const source of list) {
      if (!source.id) throw new LevelFormatError(`entity '${source.name}' has no id`);
      if (seen.has(source.id)) throw new LevelFormatError(`duplicate entity id '${source.id}'`);
      seen.add(source.id);

      out.push({ source, parent: parentId });
      walk(source.children ?? [], entityId(source.id));
    }
  };

  walk(entities, parent);
  return out;
}

/**
 * Checks names (no path separators, unique among siblings, so paths are
 * unique) and indexes the entities for reference resolution.
 */
function indexNames(flat: readonly FlatEntity[]): Map<EntityId, NamedEntity> {
  const named = new Map<EntityId, NamedEntity>();
  const siblings = new Set<string>();

  for (const { source, parent } of flat) {
    const invalid = invalidNameReason(source.name);
    if (invalid) throw new LevelFormatError(`entity '${source.name}': ${invalid}`);

    const key = `${parent ?? ""}\u0000${source.name}`;
    if (siblings.has(key)) {
      const under = parent === null ? "the level root" : `'${named.get(parent)?.name ?? parent}'`;
      throw new LevelFormatError(`two entities named '${source.name}' under ${under}`);
    }
    siblings.add(key);
    named.set(entityId(source.id), { id: entityId(source.id), name: source.name, parent });
  }
  return named;
}

function parseComponents(
  source: ManifestEntity,
  registry: ComponentRegistry,
  names: ReadonlyMap<EntityId, NamedEntity>,
  warn: (message: string) => void,
): Map<string, ComponentData> {
  const components = new Map<string, ComponentData>();

  for (const [type, raw] of Object.entries(source.components ?? {})) {
    const schema = registry.get(type);
    if (!schema) throw new LevelFormatError(`${source.name}: unknown component '${type}'`);

    const parsed = registry.parse(type, raw);
    parsed.warnings.forEach(warn);
    components.set(type, resolveEntityRefs(schema, parsed.data, names, warn));
  }

  return components;
}

/**
 * Entity references may be authored as ids, paths or unique names; stored data
 * always holds ids. A bare name that several entities share is a warning that
 * lists the paths to use instead.
 */
function resolveEntityRefs(
  schema: ComponentSchema,
  data: ComponentData,
  names: ReadonlyMap<EntityId, NamedEntity>,
  warn: (message: string) => void,
): ComponentData {
  const resolved: Record<string, unknown> = { ...data };

  for (const [prop, propType] of Object.entries(schema.props)) {
    const value = data[prop];
    if (propType.type !== "entityRef" || typeof value !== "string") continue;

    const lookup = findEntity(names, value);
    if ("found" in lookup) {
      resolved[prop] = lookup.found;
      continue;
    }
    resolved[prop] = null;
    if ("ambiguous" in lookup) {
      warn(
        `${schema.type}.${prop}: '${value}' is ambiguous (${lookup.ambiguous.join(", ")}); use a path or an id`,
      );
    } else {
      warn(`${schema.type}.${prop}: no entity '${value}' in this level`);
    }
  }

  return Object.freeze(resolved);
}
